import React, { useState, useEffect } from "react";
import { type NodeProps, type Node, useReactFlow } from "@xyflow/react";
import { Code } from "lucide-react";
import { CodeEditorModal } from "./CodeEditorModal";
import { WorkflowBaseNode } from "../../../common/shared/WorkflowBaseNode";

export const CodeNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const [code, setCode] = useState<string>(data?.code || "");
  const [language, setLanguage] = useState<string>(data?.language || "javascript");
  const [inputs, setInputs] = useState<Record<string, string>>(data?.inputs || {});
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { setNodes } = useReactFlow();

  // Sync from saved workflow data
  useEffect(() => {
    if (data?.code !== undefined && data.code !== code) setCode(data.code);
    if (data?.language !== undefined && data.language !== language) setLanguage(data.language);
    if (data?.inputs !== undefined && JSON.stringify(data.inputs) !== JSON.stringify(inputs)) setInputs(data.inputs);
  }, [data?.code, data?.language, data?.inputs]);

  // Sync config to ReactFlow
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id !== id) return node;
        const d = node.data || {};
        if (d.code === code && d.language === language && JSON.stringify(d.inputs) === JSON.stringify(inputs)) return node;
        return { ...node, data: { ...node.data, code, language, inputs } };
      }),
    );
  }, [code, language, inputs, id, setNodes]);

  const isConfigured = !!(code && language);

  return (
    <WorkflowBaseNode
      id={id}
      data={data}
      selected={selected}
      title="Code Node"
      icon={<Code className="w-4 h-4 text-blue-600" />}
      nodeDocsType="CODE"
      themeColor="blue"
      configureLabel="Configure Code"
      isConfigured={isConfigured}
      hasSourceHandle={true}
      hasErrorHandle={true}
      validationErrors={[
        ...(!code ? ["Code is required"] : []),
        ...(!language ? ["Language is required"] : []),
      ]}
      statusText={{
        running: "Executing...",
        success: "Executed",
        error: "Execution Failed",
      }}
      onConfigure={() => setIsModalOpen(true)}
    >
      {/* CodeEditorModal uses WorkflowNodeModal internally */}
      <CodeEditorModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        code={code}
        setCode={setCode}
        language={language}
        setLanguage={setLanguage}
        inputs={inputs}
        setInputs={setInputs}
        nodeId={id}
        dataOutputs={data.outputs}
      />
    </WorkflowBaseNode>
  );
};

export default CodeNode;
